import Cell from "../WaveFunctionCollapseV2/Cell.ts";
import { Vector3 } from "three";
import { Edges } from "@react-three/drei";
import Tile from "./Tile.tsx";
import PrototypeManager from "../WaveFunctionCollapseV2/Prototype/PrototypeManager.ts";
import PrototypeImpl from "../PrototypeImpl.ts";
import { useFrame } from "@react-three/fiber";
import { AxisArrow } from "@react-three/drei/web/pivotControls/AxisArrow";
import { useState } from "react";
import { folder, useControls } from "leva";

const DIRECTIONS = {
  posX: new Vector3(1, 0, 0),
  negX: new Vector3(-1, 0, 0),
  posY: new Vector3(0, 1, 0),
  negY: new Vector3(0, -1, 0),
  posZ: new Vector3(0, 0, 1),
  negZ: new Vector3(0, 0, -1),
};

interface DebugWorldProps {
  cellSize: number;
  prototypeManager: PrototypeManager;
}

export default function DebugWorld({
  cellSize,
  prototypeManager,
}: DebugWorldProps): JSX.Element {
  const gridMapCoordinatesTo3DWorldCoordinates = (
    x: number,
    y: number,
    z: number,
  ): Vector3 => {
    return new Vector3(x * cellSize, y * cellSize + 0.5, z * cellSize);
  };

  const { tileId, posX, negX, posY, negY, posZ, negZ } = useControls(
    "DebugWorld",
    {
      tileId: {
        options: prototypeManager.prototypes.map((p) => p.id),
      },
      "show neighbours": folder(
        {
          posX: true,
          negX: true,
          posY: true,
          negY: true,
          posZ: true,
          negZ: true,
        },
        { collapsed: true },
      ),
    },
  );

  const prototype = prototypeManager.getPrototypeById(tileId) as PrototypeImpl;

  console.log(cellSize);

  return (
    <>
      <axesHelper args={[5]} />

      {posX &&
        prototype.valid_neighbours.posX.map((neighbour, index) => {
          const pos = gridMapCoordinatesTo3DWorldCoordinates(
            1.5 * (index + 1),
            0,
            0,
          );
          if (neighbour === "p-1") {
            return (
              <mesh position={pos} key={`tile-${neighbour}-${index}`}>
                <boxGeometry
                  args={[cellSize - 0.1, cellSize - 0.1, cellSize - 0.1]}
                />
                <meshStandardMaterial
                  transparent
                  color={"yellow"}
                  opacity={0.1}
                />
                <Edges linewidth={1} threshold={15} color={"black"} />
              </mesh>
            );
          }
          return (
            <Tile
              key={`tile-${neighbour}-${index}`}
              tilePosition={pos}
              tileSize={1}
              prototype={
                prototypeManager.getPrototypeById(neighbour) as PrototypeImpl
              }
            />
          );
        })}

      {negX &&
        prototype.valid_neighbours.negX.map((neighbour, index) => {
          const pos = gridMapCoordinatesTo3DWorldCoordinates(
            -1.5 * (index + 1),
            0,
            0,
          );
          if (neighbour === "p-1") {
            return (
              <mesh position={pos} key={`tile-${neighbour}-${index}`}>
                <boxGeometry
                  args={[cellSize - 0.1, cellSize - 0.1, cellSize - 0.1]}
                />
                <meshStandardMaterial
                  transparent
                  color={"yellow"}
                  opacity={0.1}
                />
                <Edges linewidth={1} threshold={15} color={"black"} />
              </mesh>
            );
          }
          return (
            <Tile
              key={`tile-${neighbour}-${index}`}
              tilePosition={pos}
              tileSize={1}
              prototype={
                prototypeManager.getPrototypeById(neighbour) as PrototypeImpl
              }
            />
          );
        })}

      {posY &&
        prototype.valid_neighbours.posY.map((neighbour, index) => {
          const pos = gridMapCoordinatesTo3DWorldCoordinates(
            0,
            1.5 * (index + 1),
            0,
          );
          if (neighbour === "p-1") {
            return (
              <mesh position={pos} key={`tile-${neighbour}-${index}`}>
                <boxGeometry
                  args={[cellSize - 0.1, cellSize - 0.1, cellSize - 0.1]}
                />
                <meshStandardMaterial
                  transparent
                  color={"yellow"}
                  opacity={0.1}
                />
                <Edges linewidth={1} threshold={15} color={"black"} />
              </mesh>
            );
          }
          return (
            <Tile
              key={`tile-${neighbour}-${index}`}
              tilePosition={pos}
              tileSize={1}
              prototype={
                prototypeManager.getPrototypeById(neighbour) as PrototypeImpl
              }
            />
          );
        })}

      {negY &&
        prototype.valid_neighbours.negY.map((neighbour, index) => {
          const pos = gridMapCoordinatesTo3DWorldCoordinates(
            0,
            -1.5 * (index + 1),
            0,
          );
          if (neighbour === "p-1") {
            return (
              <mesh position={pos} key={`tile-${neighbour}-${index}`}>
                <boxGeometry
                  args={[cellSize - 0.1, cellSize - 0.1, cellSize - 0.1]}
                />
                <meshStandardMaterial
                  transparent
                  color={"yellow"}
                  opacity={0.1}
                />
                <Edges linewidth={1} threshold={15} color={"black"} />
              </mesh>
            );
          }
          return (
            <Tile
              key={`tile-${neighbour}-${index}`}
              tilePosition={pos}
              tileSize={1}
              prototype={
                prototypeManager.getPrototypeById(neighbour) as PrototypeImpl
              }
            />
          );
        })}

      {posZ &&
        prototype.valid_neighbours.posZ.map((neighbour, index) => {
          const pos = gridMapCoordinatesTo3DWorldCoordinates(
            0,
            0,
            1.5 * (index + 1),
          );
          if (neighbour === "p-1") {
            return (
              <mesh position={pos} key={`tile-${neighbour}-${index}`}>
                <boxGeometry
                  args={[cellSize - 0.1, cellSize - 0.1, cellSize - 0.1]}
                />
                <meshStandardMaterial
                  transparent
                  color={"yellow"}
                  opacity={0.1}
                />
                <Edges linewidth={1} threshold={15} color={"black"} />
              </mesh>
            );
          }
          return (
            <Tile
              key={`tile-${neighbour}-${index}`}
              tilePosition={pos}
              tileSize={1}
              prototype={
                prototypeManager.getPrototypeById(neighbour) as PrototypeImpl
              }
            />
          );
        })}

      {negZ &&
        prototype.valid_neighbours.negZ.map((neighbour, index) => {
          const pos = gridMapCoordinatesTo3DWorldCoordinates(
            0,
            0,
            -1.5 * (index + 1),
          );
          if (neighbour === "p-1") {
            return (
              <mesh position={pos} key={`tile-${neighbour}-${index}`}>
                <boxGeometry
                  args={[cellSize - 0.1, cellSize - 0.1, cellSize - 0.1]}
                />
                <meshStandardMaterial
                  transparent
                  color={"yellow"}
                  opacity={0.1}
                />
                <Edges linewidth={1} threshold={15} color={"black"} />
              </mesh>
            );
          }
          return (
            <Tile
              key={`tile-${neighbour}-${index}`}
              tilePosition={pos}
              tileSize={1}
              prototype={
                prototypeManager.getPrototypeById(neighbour) as PrototypeImpl
              }
            />
          );
        })}

      <Tile
        key="tile-0"
        tilePosition={gridMapCoordinatesTo3DWorldCoordinates(0, 0, 0)}
        tileSize={1}
        prototype={prototype}
      />
    </>
  );
}
